import { useState, useEffect, useRef, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import ChatMessage from '../components/common/ChatMessage';
import { UploadTrainingDocModal } from '../components/modals/UploadTrainingDocModal';
import { sendAIMessage, getChatHistory, getRecentSessions, getTrainingDocuments } from '../services/aiTrainingService';
import { ROLE_META } from '../utils/constants';
import { Icon } from '../components/common/Icon';

/**
 * AI Training Page - Interactive AI Mentor Chat
 */
export default function AITrainingPage() {
  const ctx = useContext(AppContext);
  const { currentUser, showToast } = ctx;
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);
  const [mode, setMode] = useState('chat'); // 'chat' or 'lecture'
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [remainingQuestions, setRemainingQuestions] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load training documents and recent sessions on mount
  useEffect(() => {
    loadDocuments();
    loadRecentSessions();
  }, []);

  // Restore session from localStorage
  useEffect(() => {
    const savedSession = localStorage.getItem('ai_training_session');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        setSessionId(session.id);
        // Could load chat history here if needed
      } catch (err) {
        console.error('Error restoring session:', err);
      }
    } else {
      // Create new session
      setSessionId(crypto.randomUUID());
    }
  }, []);

  // Save session to localStorage
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('ai_training_session', JSON.stringify({ id: sessionId }));
    }
  }, [sessionId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadDocuments = async () => {
    try {
      const docs = await getTrainingDocuments();
      setDocuments(docs);
    } catch (err) {
      console.error('Failed to load documents:', err);
    }
  };

  const loadRecentSessions = async () => {
    try {
      const sessions = await getRecentSessions(currentUser?.id);
      setRecentSessions(sessions);
    } catch (err) {
      console.error('Failed to load recent sessions:', err);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    
    const userMessage = input.trim();
    setInput('');
    setLoading(true);
    
    // Add user message immediately
    setMessages(prev => [...prev, {
      text: userMessage,
      isUser: true,
      timestamp: new Date().toISOString()
    }]);
    
    try {
      // Build conversation history (last 10 messages for context)
      const conversationHistory = messages.slice(-10).map(msg => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.text
      }));
      
      // Send to AI
      const response = await sendAIMessage(
        userMessage,
        sessionId,
        currentUser,
        conversationHistory,
        mode,
        selectedDocument?.id
      );
      
      // Update remaining questions
      if (response.remainingQuestions !== undefined) {
        setRemainingQuestions(response.remainingQuestions);
      }
      
      // Add AI response
      setMessages(prev => [...prev, {
        text: response.answer,
        isUser: false,
        timestamp: new Date().toISOString()
      }]);
      
    } catch (err) {
      console.error('AI chat error:', err);
      setMessages(prev => [...prev, {
        text: 'عذراً، حدث خطأ في معالجة طلبك. يرجى المحاولة مرة أخرى.',
        isUser: false,
        timestamp: new Date().toISOString(),
        isError: true
      }]);
      showToast('❌ فشل في إرسال الرسالة', 'error');
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startNewSession = () => {
    setSessionId(crypto.randomUUID());
    setMessages([]);
    setSelectedDocument(null);
    localStorage.setItem('ai_training_session', JSON.stringify({ id: sessionId }));
    showToast('✅ تم بدء محادثة جديدة');
  };

  const roleMeta = {
    admin: { label: 'المدير التنفيذي', icon: '👑' },
    marketer: { label: 'منسّقة صفقات', icon: '📣' },
    data_entry: { label: 'مدخل بيانات', icon: '⌨️' },
    sales_manager: { label: 'مدير مبيعات', icon: '🎯' },
    accountant: { label: 'محاسب', icon: '💼' },
    rep: { label: 'مندوب ميداني', icon: '🤝' },
  };

  const rm = roleMeta[currentUser?.role] || { label: currentUser?.role, icon: '👤' };

  return (
    <div className="ai-page" style={{ animation: 'sIn 0.3s ease', height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ color: '#000000', fontSize: 20, fontWeight: 900, marginBottom: 4 }}>
              🎓 التدريب الذكي
            </h1>
            <p style={{ color: '#3d5470', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              مرحباً {currentUser?.name} (
              <Icon name={rm.icon} size={14} color={rm.color} />
              <span>{rm.label}</span>
              ) - كيف يمكنني مساعدتك اليوم؟
            </p>
          </div>
          <div className="page-actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Remaining Questions Counter */}
            {remainingQuestions !== null && (
              <div style={{
                background: remainingQuestions > 10 ? '#ffffff' : remainingQuestions > 5 ? '#ffffff' : '#ffffff',
                border: `1px solid ${remainingQuestions > 10 ? '#10b981' : remainingQuestions > 5 ? '#f59e0b' : '#ef4444'}`,
                borderRadius: 10,
                padding: '6px 12px',
                fontSize: 12,
                color: remainingQuestions > 10 ? '#10b981' : remainingQuestions > 5 ? '#f59e0b' : '#ef4444',
                fontWeight: 700
              }}>
                ❓ {remainingQuestions} سؤال متبقي اليوم
              </div>
            )}
            
            {/* Mode Toggle */}
            <div style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              padding: 4,
              display: 'flex',
              gap: 4
            }}>
              <button
                onClick={() => setMode('chat')}
                style={{
                  background: mode === 'chat' ? '#e2e8f0' : 'transparent',
                  border: 'none',
                  borderRadius: 8,
                  padding: '6px 14px',
                  color: mode === 'chat' ? '#4ecdc4' : '#4d6680',
                  cursor: 'pointer',
                  fontFamily: "'Cairo',sans-serif",
                  fontSize: 12,
                  fontWeight: 700,
                  transition: 'all 0.2s'
                }}
              >
                💬 محادثة
              </button>
              <button
                onClick={() => setMode('lecture')}
                style={{
                  background: mode === 'lecture' ? '#e2e8f0' : 'transparent',
                  border: 'none',
                  borderRadius: 8,
                  padding: '6px 14px',
                  color: mode === 'lecture' ? '#a78bfa' : '#4d6680',
                  cursor: 'pointer',
                  fontFamily: "'Cairo',sans-serif",
                  fontSize: 12,
                  fontWeight: 700,
                  transition: 'all 0.2s'
                }}
              >
                📚 شرح
              </button>
            </div>
            
            <button
              onClick={startNewSession}
              style={{
                background: '#e2e8f0',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '8px 16px',
                color: '#4ecdc4',
                cursor: 'pointer',
                fontFamily: "'Cairo',sans-serif",
                fontSize: 13,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              ✨ محادثة جديدة
            </button>
          </div>
        </div>
        
        {/* Document Count */}
        <div style={{ 
          background: '#ffffff', 
          borderRadius: 10, 
          padding: '8px 14px', 
          border: '1px solid #e2e8f0',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          color: '#000000'
        }}>
          📚 المواد التدريبية المتاحة: <strong style={{ color: '#10b981' }}>{documents.length} وثيقة</strong>
        </div>
        
        {/* Upload Button - Admin Only */}
        {currentUser?.role === 'admin' && (
          <button
            onClick={() => setUploadModalOpen(true)}
            style={{
              background: 'linear-gradient(135deg, #059669, #10b981)',
              border: 'none',
              borderRadius: 10,
              padding: '8px 16px',
              color: '#fff',
              cursor: 'pointer',
              fontFamily: "'Cairo',sans-serif",
              fontSize: 12,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.2s'
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            📄 رفع مستند تدريبي
          </button>
        )}
      </div>

      {/* Chat Area */}
      <div style={{
        flex: 1,
        background: '#ffffff',
        borderRadius: 16,
        border: '1px solid #e2e8f0',
        padding: 20,
        overflowY: 'auto',
        marginBottom: 16
      }}>
        {messages.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            color: '#3d5470'
          }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🎓</div>
            <h2 style={{ color: '#000000', fontSize: 22, fontWeight: 900, marginBottom: 8 }}>
              مرحباً بك في التدريب الذكي!
            </h2>
            <p style={{ fontSize: 14, marginBottom: 24 }}>
              أنا مساعدك الذكي المدرب على مواد GoSnel التدريبية.<br />
              اسألني أي سؤال وسأجيب بناءً على دورك ({rm.label})
            </p>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: 12,
              maxWidth: 600,
              margin: '0 auto'
            }}>
              {[
                'كيف أقوم بتسجيل مطعم جديد؟',
                'ما هي خطوات المراجعة الميدانية؟',
                'كيف أحسب العمولة الخاصة بي؟',
                'ما هي سياسة الإرجاع؟'
              ].map((q, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setInput(q);
                    inputRef.current?.focus();
                  }}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                    padding: '12px 16px',
                    color: '#000000',
                    cursor: 'pointer',
                    fontFamily: "'Cairo',sans-serif",
                    fontSize: 13,
                    textAlign: 'right',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#e2e8f0';
                    e.currentTarget.style.borderColor = '#4ecdc4';
                    e.currentTarget.style.color = '#f1f5f9';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.color = '#94a3b8';
                  }}
                >
                  💡 {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => (
              <ChatMessage
                key={idx}
                message={msg.text}
                isUser={msg.isUser}
                timestamp={msg.timestamp}
              />
            ))}
            {loading && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 8, 
                padding: '12px 16px',
                color: '#000000',
                fontSize: 14
              }}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #4ecdc4, #38b2ac)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18
                }}>
                  🎓
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <span style={{ animation: 'pulse 1.4s infinite', animationDelay: '0s' }}>●</span>
                  <span style={{ animation: 'pulse 1.4s infinite', animationDelay: '0.2s' }}>●</span>
                  <span style={{ animation: 'pulse 1.4s infinite', animationDelay: '0.4s' }}>●</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area - DISABLED (Coming Soon) */}
      <div style={{ 
        background: '#ffffff', 
        borderRadius: 16, 
        border: '1px solid #e2e8f0',
        padding: 16,
        position: 'relative',
        opacity: 0.5,
        pointerEvents: 'none'
      }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="اكتب سؤالك هنا... (Shift+Enter لسطر جديد)"
            disabled={true}
            rows={1}
            style={{
              flex: 1,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: '12px 16px',
              color: '#000000',
              fontSize: 14,
              fontFamily: "'Cairo',sans-serif",
              outline: 'none',
              resize: 'none',
              minHeight: 44,
              maxHeight: 120,
              cursor: 'not-allowed'
            }}
          />
          <button
            onClick={handleSend}
            disabled={true}
            style={{
              background: '#e2e8f0',
              border: 'none',
              borderRadius: 12,
              padding: '12px 24px',
              color: '#fff',
              cursor: 'not-allowed',
              fontFamily: "'Cairo',sans-serif",
              fontSize: 14,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              opacity: 0.5
            }}
          >
            📤 إرسال
          </button>
        </div>
        
        {/* Coming Soon Badge */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          color: '#fff',
          padding: '8px 20px',
          borderRadius: 20,
          fontSize: 14,
          fontWeight: 700,
          fontFamily: "'Cairo',sans-serif",
          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
          whiteSpace: 'nowrap',
          pointerEvents: 'none'
        }}>
          🚧 قريباً - قيد التطوير
        </div>
      </div>
      
      {/* Upload Modal */}
      {uploadModalOpen && (
        <UploadTrainingDocModal
          isOpen={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          currentUser={currentUser}
          showToast={showToast}
        />
      )}
    </div>
  );
}
